import {useDraggable} from '@dnd-kit/core'
import {useFormLayoutPalette} from '@/hooks/useFormLayoutPalette'
import {useI18n} from '@/i18n'
import {Skeleton} from '@/components/ui/skeleton'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {cn} from '@/lib/utils'
import type {LayoutSchema, LayoutField, PaletteEntityField, PaletteAttributeDefinition, PaletteRelationshipField} from '@/types/form-layout'

// ============================================================================
// Helper: check if a field is already in the layout
// ============================================================================

function isEntityFieldInLayout(schema: LayoutSchema, fieldKey: string): boolean {
    return schema.sections.some((s) =>
        s.fields.some((f) => f.source === 'entity' && f.fieldKey === fieldKey)
    )
}

function isAttrFieldInLayout(schema: LayoutSchema, definitionId: string): boolean {
    return schema.sections.some((s) =>
        s.fields.some((f) => f.source === 'attribute' && f.definitionId === definitionId)
    )
}

function isRelFieldInLayout(schema: LayoutSchema, relationshipType: string): boolean {
    return schema.sections.some((s) =>
        s.fields.some((f) => f.source === 'relationship' && f.relationshipType === relationshipType)
    )
}

// ============================================================================
// Draggable palette item wrapper
// ============================================================================

interface DraggablePaletteItemProps {
    id: string
    field: LayoutField
    disabled: boolean
    children: React.ReactNode
}

function DraggablePaletteItem({id, field, disabled, children}: DraggablePaletteItemProps) {
    const {attributes, listeners, setNodeRef, isDragging} = useDraggable({
        id,
        /* eslint-disable-next-line i18next/no-literal-string */
        data: {type: 'palette-item', field},
        disabled,
    })

    return (
        <div
            id={id}
            ref={setNodeRef}
            {...(disabled ? {} : attributes)}
            {...(disabled ? {} : listeners)}
            className={cn(
                'rounded border bg-background px-3 py-2 text-sm transition-colors',
                disabled
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-grab hover:bg-muted/60 active:cursor-grabbing',
                isDragging && 'opacity-50'
            )}
        >
            {children}
        </div>
    )
}

// ============================================================================
// Section header
// ============================================================================

function PaletteSectionHeader({label}: {label: string}) {
    return (
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
        </div>
    )
}

// ============================================================================
// Entity field item
// ============================================================================

function EntityFieldItem({field, schema}: {field: PaletteEntityField; schema: LayoutSchema}) {
    const alreadyInLayout = isEntityFieldInLayout(schema, field.fieldKey)
    const paletteField: LayoutField = {source: 'entity', fieldKey: field.fieldKey}
    return (
        <DraggablePaletteItem
            id={`palette-entity-${field.fieldKey}`}
            field={paletteField}
            disabled={alreadyInLayout}
        >
            <div className="font-medium">{field.label}</div>
            <div className="text-xs text-muted-foreground font-mono">{field.fieldKey}</div>
        </DraggablePaletteItem>
    )
}

// ============================================================================
// Attribute field item
// ============================================================================

function AttributeFieldItem({
    definition,
    schema,
    readOnly,
}: {
    definition: PaletteAttributeDefinition
    schema: LayoutSchema
    readOnly: boolean
}) {
    const alreadyInLayout = isAttrFieldInLayout(schema, definition.id)
    const paletteField: LayoutField = {
        source: 'attribute',
        definitionId: definition.id,
        disabled: readOnly ? true : undefined,
    }
    return (
        <DraggablePaletteItem
            id={`palette-attr-${definition.id}`}
            field={paletteField}
            disabled={alreadyInLayout}
        >
            <div className="flex items-center justify-between">
                <span className="font-medium">{definition.displayName}</span>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                {readOnly && <Badge variant="outline" className="text-xs">RO</Badge>}
            </div>
            <div className="text-xs text-muted-foreground font-mono">{definition.name}</div>
        </DraggablePaletteItem>
    )
}

// ============================================================================
// Relationship field item
// ============================================================================

function RelationshipFieldItem({field, schema}: {field: PaletteRelationshipField; schema: LayoutSchema}) {
    const alreadyInLayout = isRelFieldInLayout(schema, field.relationshipType)
    const paletteField: LayoutField = {source: 'relationship', relationshipType: field.relationshipType}
    return (
        <DraggablePaletteItem
            id={`palette-rel-${field.relationshipType}`}
            field={paletteField}
            disabled={alreadyInLayout}
        >
            <div className="font-medium">{field.fromSideLabel ?? field.relationshipType}</div>
            <div className="text-xs text-muted-foreground">{field.toPartyType}</div>
        </DraggablePaletteItem>
    )
}

// ============================================================================
// Loading skeleton
// ============================================================================

function PaletteLoadingSkeleton() {
    return (
        <div className="space-y-2 p-3">
            {Array.from({length: 5}).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
            ))}
        </div>
    )
}

// ============================================================================
// Error state
// ============================================================================

function PaletteErrorState({onRetry}: {onRetry: () => void}) {
    const i18n = useI18n()
    return (
        <div className="flex flex-col items-center gap-2 p-4 text-center">
            <p className="text-sm text-muted-foreground">{i18n.formLayouts.paletteError}</p>
            <Button data-testid="palette-retry-btn" variant="outline" size="sm" onClick={onRetry}>
                {i18n.common.retry}
            </Button>
        </div>
    )
}

// ============================================================================
// Main FieldPalette component
// ============================================================================

interface FieldPaletteProps {
    useCaseKey: string
    schema: LayoutSchema
}

export function FieldPalette({useCaseKey, schema}: FieldPaletteProps) {
    const i18n = useI18n()
    const {palette, isLoading, isError, refetch} = useFormLayoutPalette(useCaseKey)

    if (isLoading) return <PaletteLoadingSkeleton />
    if (isError || !palette) return <PaletteErrorState onRetry={() => void refetch()} />

    return (
        <div className="space-y-4 p-3">
            {/* Entity fields */}
            {palette.entityFields.length > 0 && (
                <div>
                    <PaletteSectionHeader label={i18n.formLayouts.paletteEntityFields} />
                    <div className="space-y-1">
                        {palette.entityFields.map((f) => (
                            <EntityFieldItem key={f.fieldKey} field={f} schema={schema} />
                        ))}
                    </div>
                </div>
            )}

            {/* IAM-managed attribute definitions */}
            {palette.attributeDefinitions.length > 0 && (
                <div>
                    <PaletteSectionHeader label={i18n.formLayouts.paletteIamDefs} />
                    <div className="space-y-1">
                        {palette.attributeDefinitions.map((d) => (
                            <AttributeFieldItem key={d.id} definition={d} schema={schema} readOnly={false} />
                        ))}
                    </div>
                </div>
            )}

            {/* HR read-only definitions */}
            {palette.readOnlyFields.length > 0 && (
                <div>
                    <PaletteSectionHeader label={i18n.formLayouts.paletteReadOnly} />
                    <div className="space-y-1">
                        {palette.readOnlyFields.map((d) => (
                            <AttributeFieldItem key={d.id} definition={d} schema={schema} readOnly />
                        ))}
                    </div>
                </div>
            )}

            {/* Relationship fields (party-relationship use-case only) */}
            {palette.relationshipFields.length > 0 && (
                <div>
                    <PaletteSectionHeader label={i18n.formLayouts.paletteRelationships} />
                    <div className="space-y-1">
                        {palette.relationshipFields.map((f) => (
                            <RelationshipFieldItem key={f.relationshipType} field={f} schema={schema} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
